import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Card,
  CardContent,
  Avatar,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CodeIcon from "@mui/icons-material/Code";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import BoltIcon from "@mui/icons-material/Bolt"; // Agrega este import si no está
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MainMenu from "./MainMenu";
import { useNavigate } from "react-router-dom";

const benefits = [
  {
    icon: <AccessTimeIcon sx={{ color: "#38bdf8", fontSize: 38 }} />,
    title: "Disponible 24/7 en tu web",
    color: "#38bdf8",
    bg: "linear-gradient(120deg, #1e293b 0%, #334155 100%)",
    description:
      "Tus visitantes reciben respuestas inmediatas en cualquier momento, mejorando la experiencia y reduciendo el rebote.",
  },
  {
    icon: <ThumbUpAltIcon sx={{ color: "#6366f1", fontSize: 38 }} />,
    title: "Convierte más visitantes en clientes",
    color: "#6366f1",
    bg: "linear-gradient(120deg, #1e293b 0%, #6366f1 100%)",
    description:
      "El chatbot guía, responde dudas y califica prospectos, ayudando a captar más leads y ventas desde tu web.",
  },
  {
    icon: <SupportAgentIcon sx={{ color: "#34d399", fontSize: 38 }} />,
    title: "Reduce carga de soporte",
    color: "#34d399",
    bg: "linear-gradient(120deg, #1e293b 0%, #34d399 100%)",
    description:
      "Automatiza preguntas frecuentes y tareas repetitivas, permitiendo que tu equipo se enfoque en casos complejos.",
  },
];

// Mensajes para la animación del chat
const chatMessages = [
  {
    from: "bot",
    text: "¡Hola! 👋 ¿En qué puedo ayudarte hoy?",
  },
  {
    from: "user",
    text: "¿Puedo integrar el chatbot en mi web fácilmente?",
  },
  {
    from: "bot",
    text: "¡Sí! Solo copia y pega el código que te damos y tu widget estará listo en segundos.",
  },
  {
    from: "user",
    text: "¿Puedo personalizar el diseño?",
  },
  {
    from: "bot",
    text: "¡Por supuesto! Puedes adaptar colores, textos y mucho más desde tu panel.",
  },
  {
    from: "user",
    text: "¡Genial, gracias!",
  },
];

function ChatSimulation() {
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [currentMsgIdx, setCurrentMsgIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [cycle, setCycle] = useState(0);
  const chatEndRef = useRef(null);

  useEffect(() => {
    let typingTimeout;
    let nextMsgTimeout;
    let resetTimeout;

    if (currentMsgIdx < chatMessages.length) {
      setTyping(true);
      const currentMsg = chatMessages[currentMsgIdx];
      if (charIdx < currentMsg.text.length) {
        typingTimeout = setTimeout(() => {
          setDisplayedMessages((prev) => {
            const newMsgs = [...prev];
            if (newMsgs[currentMsgIdx]) {
              newMsgs[currentMsgIdx].text = currentMsg.text.slice(0, charIdx + 1);
            } else {
              newMsgs.push({ ...currentMsg, text: currentMsg.text[0] });
            }
            return newMsgs;
          });
          setCharIdx((c) => c + 1);
        }, 30);
      } else {
        setTyping(false);
        nextMsgTimeout = setTimeout(() => {
          setCurrentMsgIdx((idx) => idx + 1);
          setCharIdx(0);
        }, 700);
      }
    } else {
      resetTimeout = setTimeout(() => {
        setDisplayedMessages([]);
        setCurrentMsgIdx(0);
        setCharIdx(0);
        setCycle((c) => c + 1);
      }, 10000);
    }

    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(nextMsgTimeout);
      clearTimeout(resetTimeout);
    };
  }, [currentMsgIdx, charIdx, cycle]);

  // Render de burbujas
  return (
    <Box
      sx={{
        width: 420,
        height: 520,
        background: "linear-gradient(120deg, #f1f5f9 0%, #e0e7ef 100%)",
        borderRadius: 4,
        boxShadow: "0 4px 32px 0 rgba(56,189,248,0.13)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header del chat */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: "linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)",
          color: "#fff",
          borderRadius: 3,
          px: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Avatar sx={{ bgcolor: "#fff", color: "#6366f1", mr: 1, width: 36, height: 36 }}>
          <LanguageIcon />
        </Avatar>
        <Typography fontWeight={700} fontSize={16}>
          NARA Chatbot
        </Typography>
        <Box sx={{ ml: "auto", fontSize: 12, opacity: 0.7 }}>Online</Box>
      </Box>
      {/* Mensajes animados */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mb: 2,
          overflowY: "hidden",
          cursor: "grab",
        }}
      >
        {displayedMessages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              justifyContent: msg.from === "user" ? "flex-end" : "flex-start",
              alignItems: "flex-end",
            }}
          >
            {msg.from === "bot" && (
              <Avatar sx={{ bgcolor: "#6366f1", width: 28, height: 28, mr: 1, mt: "auto" }}>
                <LanguageIcon sx={{ fontSize: 18 }} />
              </Avatar>
            )}
            <Box
              sx={{
                background:
                  msg.from === "bot"
                    ? "linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)"
                    : "#e0e7ef",
                color: msg.from === "bot" ? "#fff" : "#334155",
                borderRadius: 3,
                px: 2,
                py: 1,
                maxWidth: 290,
                fontSize: 16,
                boxShadow:
                  msg.from === "bot"
                    ? "0 2px 8px #6366f133"
                    : "0 2px 8px #94a3b833",
                ml: msg.from === "user" ? 0 : undefined,
                mr: msg.from === "user" ? 1 : undefined,
                wordBreak: "break-word",
              }}
            >
              {msg.text}
            </Box>
          </Box>
        ))}
        {typing && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <Box
              sx={{
                width: 36,
                height: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  bgcolor: "#6366f1",
                  borderRadius: "50%",
                  animation: "blink 1s infinite alternate",
                  mx: 0.2,
                }}
              />
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  bgcolor: "#6366f1",
                  borderRadius: "50%",
                  animation: "blink 1s infinite alternate 0.2s",
                  mx: 0.2,
                }}
              />
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  bgcolor: "#6366f1",
                  borderRadius: "50%",
                  animation: "blink 1s infinite alternate 0.4s",
                  mx: 0.2,
                }}
              />
            </Box>
            <style>
              {`
                @keyframes blink {
                  0% { opacity: 0.3; }
                  100% { opacity: 1; }
                }
              `}
            </style>
          </Box>
        )}
        <div ref={chatEndRef} />
      </Box>
      {/* Input simulado */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: "#fff",
          borderRadius: 2,
          px: 2,
          py: 1,
          boxShadow: "0 1px 4px #94a3b822",
        }}
      >
        <Box sx={{ flex: 1, color: "#64748b", fontSize: 15, opacity: 0.7 }}>
          Escribe un mensaje...
        </Box>
        <Button
          variant="contained"
          size="small"
          sx={{
            ml: 1,
            minWidth: 0,
            px: 2,
            py: 0.5,
            background: "linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)",
            color: "#fff",
            borderRadius: 2,
            fontWeight: 700,
            textTransform: "none",
            fontSize: 14,
            boxShadow: "none",
            "&:hover": {
              background: "linear-gradient(90deg, #0ea5e9 0%, #6366f1 100%)",
            },
          }}
        >
          Enviar
        </Button>
      </Box>
    </Box>
  );
}

export default function WebsiteChatbotInfo() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(120deg, #0f172a 0%, #1e293b 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Fondo animado */}
      <Box
        sx={{
          position: "absolute",
          width: "180vw",
          height: "180vw",
          left: "-40vw",
          top: "-60vw",
          background:
            "radial-gradient(circle at 60% 40%, #6366f1 0%, transparent 60%), radial-gradient(circle at 30% 80%, #0ea5e9 0%, transparent 60%)",
          filter: "blur(120px)",
          opacity: 0.35,
          zIndex: 0,
        }}
      />
      <MainMenu />
      {/* Hero principal */}
      <Box
        sx={{
          mt: 10,
          textAlign: "center",
          zIndex: 2,
          position: "relative",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            maxWidth: 800,
            mx: "auto",
            background: "rgba(255,255,255,0.98)",
            mb: 6,
            boxShadow: "0 8px 32px 0 rgba(56,189,248,0.13)",
          }}
        >
          <LanguageIcon sx={{ color: "#6366f1", fontSize: 64, mb: 2 }} />
          <Typography
            variant="h3"
            fontWeight={900}
            color="#6366f1"
            mb={2}
            sx={{ letterSpacing: 1 }}
          >
            Chatbot para tu Sitio Web
          </Typography>
          <Typography
            variant="h5"
            color="#0ea5e9"
            fontWeight={700}
            sx={{ mb: 2 }}
          >
            Instala un widget conversacional en minutos y mejora la experiencia digital.
          </Typography>
          <Typography
            variant="body1"
            color="#334155"
            sx={{ fontSize: 18, mb: 2 }}
          >
            Agrega un chatbot inteligente a tu página web con solo copiar y pegar un pequeño código. El widget se adapta al diseño de tu sitio y responde automáticamente a tus visitantes, guiando, resolviendo dudas y captando leads sin esfuerzo.
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f1f5f9",
              borderRadius: 2,
              p: 2,
              mb: 2,
              mt: 2,
              maxWidth: 600,
              mx: "auto",
              fontFamily: "monospace",
              fontSize: 15,
              color: "#334155",
              boxShadow: "0 2px 8px #0ea5e933",
            }}
          >
            <CodeIcon sx={{ color: "#6366f1", mr: 1 }} />
            {`<script src="https://nara.ai/widget.js"></script>`}
          </Box>
          <Typography
            variant="body2"
            color="#64748b"
            sx={{ mb: 3 }}
          >
            Solo copia y pega este código antes del cierre de <b>&lt;/body&gt;</b> en tu web. ¡Así de fácil!
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: "linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)",
              fontWeight: 700,
              color: "#fff",
              borderRadius: 2,
              px: 5,
              py: 1.5,
              fontSize: 20,
              boxShadow: "0 2px 16px #38bdf8",
              textTransform: "none",
              letterSpacing: 1,
              "&:hover": {
                background: "linear-gradient(90deg, #0ea5e9 0%, #6366f1 100%)",
                transform: "scale(1.04)",
              },
            }}
            onClick={() => navigate("/constructor")}
          >
            Prueba gratis para tu Web
          </Button>
        </Paper>

        {/* Beneficios en Cards horizontales */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          justifyContent="center"
          alignItems="stretch"
          sx={{
            maxWidth: 1200,
            mx: "auto",
            mb: 10,
            mt: 2,
            px: { xs: 2, md: 0 },
          }}
        >
          {benefits.map((benefit, idx) => (
            <Card
              key={idx}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                borderRadius: 5,
                boxShadow: "0 6px 24px 0 rgba(56,189,248,0.13)",
                border: `2px solid ${benefit.color}`,
                background: benefit.bg,
                color: "#cbd5e1",
                minHeight: 180,
                maxWidth: 400,
                mx: "auto",
                transition: "transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s",
                "&:hover": {
                  transform: "scale(1.045) translateY(-8px)",
                  boxShadow: `0 12px 40px 0 ${benefit.color}33`,
                  borderColor: benefit.color,
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#fff",
                  width: 72,
                  height: 72,
                  boxShadow: `0 2px 16px ${benefit.color}33`,
                  ml: 3,
                  mr: 2,
                }}
              >
                {benefit.icon}
              </Avatar>
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  py: 3,
                  px: 2,
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  color={benefit.color}
                  mb={1}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="#cbd5e1"
                  sx={{ opacity: 0.92, fontSize: 17, mt: 1 }}
                >
                  {benefit.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* Sección extra: texto + simulación de chat */}
        <Grid
          container
          spacing={6}
          alignItems="center"
          justifyContent="center"
          sx={{
            maxWidth: 1200, // Cambiado de 1100 a 1200
            mx: "auto",
            mb: 10,
            px: { xs: 2, md: 0 },
          }}
        >
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                background: "rgba(30,41,59,0.92)",
                borderRadius: 4,
                boxShadow: "0 4px 32px 0 rgba(56,189,248,0.10)",
                p: { xs: 3, md: 5 },
                mt: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Typography
                  variant="h4"
                  fontWeight={900}
                  color="#38bdf8"
                  mb={2}
                  sx={{ letterSpacing: 1, textShadow: "0 2px 16px #6366f1" }}
                >
                  Convierte a tus visitantes en clientes
                </Typography>
                <Typography
                  variant="body1"
                  color="#cbd5e1"
                  sx={{ fontSize: 18 }}
                >
                  Te ayudamos a desplegar un excelente servicio al cliente en tiempo real en tu sitio web.
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <ChatSimulation />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Sección: texto + icono ventas */}
        <Grid
          container
          spacing={6}
          alignItems="center"
          justifyContent="center"
          sx={{
            maxWidth: 1200, // Cambiado de 1100 a 1200
            mx: "auto",
            mb: 10,
            px: { xs: 2, md: 0 },
          }}
        >
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row-reverse" }, // Texto a la derecha, icono a la izquierda
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                background: "rgba(30,41,59,0.92)",
                borderRadius: 4,
                boxShadow: "0 4px 32px 0 rgba(56,189,248,0.10)",
                p: { xs: 3, md: 5 },
                mt: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Typography
                  variant="h4"
                  fontWeight={900}
                  color="#38bdf8"
                  mb={2}
                  sx={{ letterSpacing: 1, textShadow: "0 2px 16px #6366f1" }}
                >
                  Transforma tu sitio web en una máquina inteligente de oportunidades
                </Typography>
                <Typography
                  variant="body1"
                  color="#cbd5e1"
                  sx={{ fontSize: 18 }}
                >
                  Con Nara Chatbots, cada visitante es guiado con precisión, empatía y velocidad. Vende mientras duermes, conecta mientras creces. El futuro de tu canal de ventas comienza ahora.
                </Typography>
              </Box>
              <MonetizationOnIcon
                sx={{
                  fontSize: { xs: 120, md: 180 },
                  color: "#38bdf8",
                  boxShadow: "0 2px 16px #38bdf855",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)",
                  p: 2,
                  m: 2,
                  display: "block",
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Nueva sección: texto + ícono de ruptura de esquemas */}
        <Grid
          container
          spacing={6}
          alignItems="center"
          justifyContent="center"
          sx={{
            maxWidth: 1200, // Igual que las cards de beneficios
            mx: "auto",
            mb: 10,
            px: { xs: 2, md: 0 },
          }}
        >
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" }, // Texto a la izquierda, icono a la derecha
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                background: "rgba(30,41,59,0.92)",
                borderRadius: 4,
                boxShadow: "0 4px 32px 0 rgba(56,189,248,0.10)",
                p: { xs: 3, md: 5 },
                mt: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 260, textAlign: { xs: "center", md: "left" } }}>
                <Typography
                  variant="h4"
                  fontWeight={900}
                  color="#38bdf8"
                  mb={2}
                  sx={{ letterSpacing: 1, textShadow: "0 2px 16px #6366f1" }}
                >
                  Olvida el soporte del pasado. Nara llegó para romper las reglas.
                </Typography>
                <Typography
                  variant="body1"
                  color="#cbd5e1"
                  sx={{ fontSize: 18, mb: 1 }}
                >
                  Asistentes automatizados que responden en segundos, encantan en minutos y están despiertos siempre —porque tus clientes no deberían esperar ni un segundo más.
                </Typography>
                <Typography
                  variant="body1"
                  color="#cbd5e1"
                  sx={{ fontSize: 18, mb: 1 }}
                >
                  24/7, sin excusas, sin fricción, sin límites.
                </Typography>
                <Typography
                  variant="body1"
                  color="#38bdf8"
                  sx={{ fontSize: 18, fontWeight: 700, mt: 2 }}
                >
                  Bienvenido al futuro del servicio: rápido, humano, imparable.
                </Typography>
              </Box>
              <BoltIcon
                sx={{
                  fontSize: { xs: 120, md: 180 },
                  color: "#fbbf24",
                  boxShadow: "0 2px 16px #fbbf2455",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)",
                  p: 2,
                  m: 2,
                  display: "block",
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Sección de Preguntas y Respuestas (FAQ) */}
        <Grid
          container
          spacing={6}
          alignItems="center"
          justifyContent="center"
          sx={{
            maxWidth: 1200,
            mx: "auto",
            mb: 10,
            px: { xs: 2, md: 0 },
          }}
        >
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                background: "rgba(30,41,59,0.92)",
                borderRadius: 4,
                boxShadow: "0 4px 32px 0 rgba(56,189,248,0.10)",
                p: { xs: 3, md: 6 },
                mt: 2,
              }}
            >
              <Typography
                variant="h4"
                fontWeight={900}
                color="#38bdf8"
                mb={3}
                sx={{ letterSpacing: 1, textShadow: "0 2px 16px #6366f1", textAlign: "center" }}
              >
                Preguntas Frecuentes
              </Typography>
              <Accordion
                sx={{
                  background: "rgba(255,255,255,0.04)",
                  color: "#cbd5e1",
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: "0 2px 12px #38bdf822",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#38bdf8" }} />}>
                  <Typography fontWeight={700} fontSize={18} color="#38bdf8">
                    ¿Qué es un chatbot?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    fontSize={18}
                    color="#cbd5e1"
                    sx={{ whiteSpace: "pre-line", textAlign: "left" }}
                  >
                    🤖 Imagina conversar con la tecnología como si hablaras con un ser humano.
                    Así nacen los chatbots: inteligencias conversacionales que no solo entienden, sino que responden, guían y acompañan al usuario en tiempo real, a través de una experiencia fluida y natural.

                    🚪 Algunos siguen caminos estructurados, como si cada decisión abriera una nueva puerta en una aventura interactiva. Son precisos, visuales, intuitivos. Otros, más evolucionados, usan inteligencia artificial y comprensión del lenguaje natural para crear conversaciones libres, auténticas y profundamente humanas.

                    🔄 El futuro no se trata de elegir entre uno u otro, sino de fusionar lo mejor de ambos mundos: estructura y empatía, eficiencia y libertad.

                    💡 Porque el verdadero avance no es solo que la máquina te entienda, sino que te escuche, se anticipe y se adapte a ti.

                    ✨ Bienvenidos a la nueva era conversacional. Una donde la tecnología no solo responde… sino que conversa, conecta y evoluciona contigo.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                sx={{
                  background: "rgba(255,255,255,0.04)",
                  color: "#cbd5e1",
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: "0 2px 12px #38bdf822",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#38bdf8" }} />}>
                  <Typography fontWeight={700} fontSize={18} color="#38bdf8">
                    ¿Cuáles son los requisitos para crear un chatbot?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    fontSize={18}
                    color="#cbd5e1"
                    sx={{ whiteSpace: "pre-line", textAlign: "left" }}
                  >
                    🚀 Hoy, crear un chatbot ya no es cosa de expertos. Es cosa de visionarios.
                    Porque cuando la tecnología se vuelve accesible, el poder de transformar el mundo se reparte entre todos.

                    👩‍⚕️👨‍🏫👩‍💼 Con Nara, cualquier persona —desde médicos, abogados, ingenieros y veterinarios, hasta emprendedores, docentes o incluso amas de casa— puede crear su propio asistente conversacional sin escribir una sola línea de código. Solo necesitas una idea, un objetivo claro y el deseo de innovar.

                    🛠️ El constructor visual de Nara te permite diseñar experiencias conversacionales de forma intuitiva: arrastrar, soltar y ver cómo tu chatbot cobra vida.

                    🎯 Pero construir no es suficiente. Los chatbots que realmente marcan la diferencia son los que entienden al usuario, lo acompañan y lo guían en el momento justo, con el mensaje preciso.

                    🧑‍💻 Y si tienes conocimientos técnicos, las puertas se abren aún más: personalización total, flujos dinámicos, experiencias inmersivas.

                    🌟 Porque el futuro no lo programan solo los programadores. Lo crean los que se atreven.

                    ✨ Con Nara, el chatbot no es el fin. Es el comienzo de una nueva forma de conectar.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                sx={{
                  background: "rgba(255,255,255,0.04)",
                  color: "#cbd5e1",
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: "0 2px 12px #38bdf822",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#38bdf8" }} />}>
                  <Typography fontWeight={700} fontSize={18} color="#38bdf8">
                    ¿Ofrecen plantillas de chatbots?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography fontSize={18} color="#cbd5e1" sx={{ textAlign: "left" }}>
                    
                    🎉 ¡Sí, el futuro puede comenzar hoy mismo!  
                    Crear un chatbot con Nara es tan sencillo como imaginarlo... pero si quieres acelerar el viaje, no estás solo.

                    📚 Explora nuestra biblioteca de plantillas inteligentes, diseñadas para resolver problemas reales y activar resultados desde el primer clic.

                    🚀 ¿Quieres generar clientes potenciales? ¿Lanzar una campaña? ¿Impulsar tu tienda online? ¿Mejorar tu atención al cliente? Ya existe una estructura lista para ti.

                   👩‍⚕️🛒 Desde médicos que automatizan citas, hasta emprendedores que venden sin fronteras, nuestras plantillas son más que atajos:  
                  son trampolines al futuro.

                  ⚡ Con Nara, crear un chatbot no es solo fácil…  
                   es poderoso, es escalable, es ahora.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                sx={{
                  background: "rgba(255,255,255,0.04)",
                  color: "#cbd5e1",
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: "0 2px 12px #38bdf822",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#38bdf8" }} />}>
                  <Typography fontWeight={700} fontSize={18} color="#38bdf8">
                    ¿Cómo puedo agregar un chatbot a mi sitio web?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography fontSize={18} color="#cbd5e1" sx={{ textAlign: "left" }}>
                    La conversación del futuro no se limita a un solo formato. Se adapta, fluye, se integra.

                    Con Nara, puedes insertar tu asistente inteligente en tu sitio web de formas tan flexibles como poderosas, creando experiencias que se sienten naturales, humanas… y mágicamente efectivas.

                    💬 Widget flotante: la clásica burbuja en la esquina inferior, lista para conversar, captar y convertir.

                    ⚡ Ventana emergente: no es solo un pop-up… es una invitación a una conversación real. Aparece justo cuando más se necesita, creando una experiencia envolvente y contextual.

                    🔗 Integración directa: coloca a tu chatbot justo en el corazón de tu sitio, en el momento exacto del viaje del usuario. Invisiblemente útil. Perfectamente integrado.

                    🌌 Pantalla completa: transforma toda una landing page en una conversación interactiva. Ideal para campañas impactantes y conversiones fuera de serie.

                    Con Nara, tu chatbot no solo aparece… se convierte en parte del diseño, del flujo, del alma de tu sitio.

                    Porque en el nuevo internet, las conversaciones son la interfaz.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}